import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Footer from '../components/Footer';
import { BackgroundBeams } from '../components/BackgroundBeams';
import {
  FaBrain, FaLaptopCode, FaMobileAlt, FaBriefcase, FaSyncAlt, FaCloud,
  FaShieldAlt, FaServer, FaCheckCircle, FaWhatsapp, FaTimes,
  FaPaintBrush, FaArrowRight
} from 'react-icons/fa';
import '../App.css';
import '../pages/ServicesPage.css'

// Data for primary service cards
const primaryServices = [
  {
    icon: <FaBrain />,
    title: 'AI Solutions',
    description: 'Leverage advanced machine learning and artificial intelligence to automate processes, gain predictive insights, and make data-driven decisions that transform your business operations.',
    tags: ['Machine Learning', 'Predictive Analytics', 'Automation'],
    detailedDescription: 'Transform your business with cutting-edge artificial intelligence that learns, adapts, and delivers exceptional results.',
    features: ['Machine Learning', 'Natural Language Processing', 'Predictive Analytics', 'Computer Vision'],
    benefits: ['40% efficiency increase', 'Cost reduction', 'Enhanced decision making', 'Automated workflows']
  },
  {
    icon: <FaLaptopCode />,
    title: 'Web Development',
    description: 'Build modern, responsive web applications with cutting-edge technologies. Fast, secure, and scalable solutions designed to deliver exceptional user experiences across all devices.',
    tags: ['React', 'Node.js', 'Cloud-Ready'],
    detailedDescription: 'Create stunning web experiences that engage users and drive conversions across all devices.',
    features: ['Responsive Design', 'Progressive Web Apps', 'API Integration', 'SEO Optimization'],
    benefits: ['Modern user experience', 'Fast loading times', 'Cross-platform compatibility', 'Better engagement']
  },
  {
    icon: <FaMobileAlt />,
    title: 'Mobile Apps',
    description: 'Native iOS and Android applications that deliver seamless performance and engaging user experiences. From concept to deployment, we build apps that users love.',
    tags: ['iOS', 'Android', 'Cross-Platform'],
    detailedDescription: 'Build native mobile applications that deliver exceptional performance and user satisfaction.',
    features: ['Native Performance', 'Offline Capability', 'Push Notifications', 'App Store Optimization'],
    benefits: ['Better user engagement', 'Increased retention', 'Brand visibility', 'Revenue growth']
  },
  {
    icon: <FaBriefcase />,
    title: 'Business Solutions',
    description: 'Custom enterprise software tailored to your unique needs. Streamline workflows, integrate systems, and gain actionable insights with solutions built for your business.',
    tags: ['Enterprise Software', 'ERP', 'Integration'],
    detailedDescription: 'Tailored software solutions designed to streamline operations and accelerate growth.',
    features: ['Workflow Automation', 'Data Analytics', 'Custom Dashboards', 'Integration Services'],
    benefits: ['Streamlined operations', 'Better insights', 'Improved productivity', 'Scalable architecture']
  },
  {
    icon: <FaPaintBrush />,
    title: 'Branding',
    description: 'Build a powerful brand identity that resonates with your audience. We create memorable visual identities and strategies that drive recognition and loyalty.',
    tags: ['Identity', 'Design', 'Strategy'],
    detailedDescription: 'Comprehensive branding solutions to define, design, and deliver a compelling brand experience.',
    features: ['Logo Design', 'Visual Identity', 'Brand Strategy', 'Guidelines'],
    benefits: ['Distinct market presence', 'Increased loyalty', 'Consistent messaging', 'Premium perception']
  },
  {
    icon: <FaShieldAlt />,
    title: 'Cyber Solutions',
    description: 'Comprehensive cybersecurity services to protect your digital assets. From threat detection to compliance, we secure your systems against evolving cyber threats.',
    tags: ['Security', 'Compliance', 'Protection'],
    detailedDescription: 'Comprehensive cybersecurity solutions to protect your digital assets and ensure compliance.',
    features: ['Threat Detection', 'Data Encryption', 'Access Control', 'Security Audits'],
    benefits: ['Prevent breaches', 'Secure data', 'Compliance assurance', 'Peace of mind']
  }
];

// Process steps
const processSteps = [
  { number: '01', title: 'Discovery', description: 'We understand your business goals and challenges' },
  { number: '02', title: 'Strategy', description: 'We design a tailored solution roadmap' },
  { number: '03', title: 'Development', description: 'We build with cutting-edge technology' },
  { number: '04', title: 'Launch', description: 'We deploy and support your success' }
];

const ServicesPage = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [showWhatsAppTooltip, setShowWhatsAppTooltip] = useState(false);

  const whatsappNumber = '+917600236927';
  const whatsappMessage = 'Hi! I would like to discuss a project with DEPLOY-X.';
  const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  React.useEffect(() => {
    const tooltipTimer = setTimeout(() => {
      setShowWhatsAppTooltip(true);
      setTimeout(() => setShowWhatsAppTooltip(false), 5000);
    }, 3000);
    return () => clearTimeout(tooltipTimer);
  }, []);

  return (
    <>
      {/* ✅ Motion Container */}
      <motion.div
        className="services-page"
        initial={{ opacity: 0, scale: 0.95, filter: "blur(10px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ duration: 1 }}
      >
        <main>
          {/* Hero Section */}
          <section className="services-hero">
            <BackgroundBeams />
            <div className="services-page-hero-overlay"></div>
            <div className="container">
              <motion.div
                className="services-page-hero-content"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <motion.span
                  className="services-page-hero-badge"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  Our Services
                </motion.span>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                >
                  Transform Your Business with <span className="gradient-text">Digital Innovation</span>
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  We provide comprehensive digital solutions to transform your business and accelerate growth through innovative technology.
                </motion.p>

                <motion.div
                  className="services-page-hero-stats"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 }}
                >
                  {[
                    { number: '50+', label: 'Projects Completed' },
                    { number: '98%', label: 'Client Satisfaction' },
                    { number: '24/7', label: 'Support Available' }
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      className="stat-item"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      <div className="stat-number">{stat.number}</div>
                      <div className="stat-label">{stat.label}</div>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* Services Grid Section */}
          <section className="services-grid-section">
            <div className="container">
              <motion.div
                className="section-header"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <span className="section-label">What We Offer</span>
                <h2>Our Core Services</h2>
                <p>Comprehensive solutions tailored to your business needs</p>
              </motion.div>

              <div className="services-grid">
                {primaryServices.map((service, index) => (
                  <motion.div
                    key={index}
                    className="service-card-container"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <motion.div
                      className="service-card-flipper"
                      animate={{ rotateY: selectedService === index ? 180 : 0 }}
                      transition={{ duration: 0.6 }}
                      style={{ transformStyle: 'preserve-3d' }}
                    >
                      {/* Front of card */}
                      <div className="service-card service-card-front">
                        <div className="service-card-header">
                          <motion.div
                            className="service-icon"
                            whileHover={{ rotate: 360, scale: 1.1 }}
                            transition={{ duration: 0.5 }}
                          >
                            {service.icon}
                          </motion.div>
                          <span className="service-number">{String(index + 1).padStart(2, '0')}</span>
                        </div>
                        <h3>{service.title}</h3>
                        <p className="service-description">{service.description}</p>

                        <motion.button
                          className="expand-button"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setSelectedService(index)}
                        >
                          Learn More <FaArrowRight />
                        </motion.button>
                      </div>

                      {/* Back of card */}
                      <div className="service-card service-card-back">
                        <div className="service-details">
                          <p className="detailed-description">{service.detailedDescription}</p>
                          <div className="features-benefits">
                            <div>
                              <h4>Key Features</h4>
                              <ul>
                                {service.features.map((feature, idx) => (
                                  <li key={idx}><FaCheckCircle /> {feature}</li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h4>Benefits</h4>
                              <ul>
                                {service.benefits.map((benefit, idx) => (
                                  <li key={idx}><FaCheckCircle /> {benefit}</li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          <motion.button
                            className="expand-button back-button"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setSelectedService(null)}
                          >
                            Back <FaArrowRight style={{ transform: 'rotate(180deg)' }} />
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Process Section */}
          <section className="process-section">
            <div className="container">
              <motion.div
                className="section-header"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <span className="section-label">How We Work</span>
                <h2>Our Process</h2>
                <p>A proven methodology that delivers exceptional results</p>
              </motion.div>

              <div className="process-grid">
                {processSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    className="process-step"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="step-number">{step.number}</div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                    {index < processSteps.length - 1 && (
                      <div className="step-connector"></div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Technologies Section */}
          <section className="tech-section">
            <div className="container">
              <motion.div
                className="section-header"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <span className="section-label">Additional Expertise</span>
                <h2>Technologies We Master</h2>
              </motion.div>

              <div className="tech-grid">
                {[
                  { icon: <FaSyncAlt />, title: 'Automation', description: 'Streamline workflows with process automation' },
                  { icon: <FaCloud />, title: 'Cloud Services', description: 'Deploy scalable cloud infrastructure' },
                  { icon: <FaShieldAlt />, title: 'Cybersecurity', description: 'Advanced security measures' },
                  { icon: <FaServer />, title: 'DevOps', description: 'CI/CD and deployment automation' }
                ].map((tech, index) => (
                  <motion.div
                    key={index}
                    className="tech-card"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -8 }}
                  >
                    <motion.div
                      className="tech-icon"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      {tech.icon}
                    </motion.div>
                    <h4>{tech.title}</h4>
                    <p>{tech.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="services-page-cta">
            <div className="container">
              <motion.div
                className="cta-content"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2>Ready to Start Your Digital Journey?</h2>
                <p>Let's discuss how our solutions can accelerate your digital transformation journey.</p>
                <motion.a
                  href="/contact"
                  className="cta-button"
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(0, 191, 255, 0.3)" }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get Started Today
                </motion.a>
              </motion.div>
            </div>
          </section>
        </main>
        <Footer />
      </motion.div>

      {/* WhatsApp Floating Button */}
      <div className="whatsapp-float-container">
        <AnimatePresence>
          {showWhatsAppTooltip && (
            <motion.div
              className="whatsapp-tooltip"
              initial={{ opacity: 0, x: 20, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.8 }}
              transition={{ duration: 0.3 }}
            >
              <button className="tooltip-close" onClick={() => setShowWhatsAppTooltip(false)}>
                <FaTimes />
              </button>
              <p className="tooltip-text">
                <strong>Need Help?</strong><br />
                Chat with us on WhatsApp!
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.a
          href={whatsappURL}
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-button"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onMouseEnter={() => setShowWhatsAppTooltip(true)}
          onMouseLeave={() => setShowWhatsAppTooltip(false)}
        >
          <FaWhatsapp className="whatsapp-icon" />
          <span className="pulse-ring"></span>
          <span className="pulse-ring pulse-ring-delay"></span>
        </motion.a>
      </div>
    </>
  );
};

export default ServicesPage;
